import { ECarts } from '@base/utils/constants'
import { useAppIndexStore } from '@base/stores/indexStore'
import { onBeforeRouteUpdate } from 'vue-router'
import { def_newProjectUrlId } from '~/utils/constants'
import type { IOpenProject } from '~/utils/types'
import { ut_wilo_projectIdFromUrl, ut_wilo_setProjectUrl } from '~/utils/priceList'
import { useProjectAutoSave } from '~/composables/projectAutoSave'
import { useProjectOpen } from '~/composables/projectOpen'

/*
 * The life of a price-list project on the cart page (moved out of Cart.vue, 14.10.2026): which project
 * is open, opening, closing and letting go of it, the lock and the name asked when it comes into
 * being, the save state, and the router hook for the back button. Cart.vue renders the page — shared
 * by every cart — and takes all of that from here.
 *
 * Called for every cart, and idle for every one but the price list: nothing here acts without an open
 * project, and the price list is the only cart that opens one.
 */
export function usePriceListProject(cart: ECarts) {
  const indexStore = useAppIndexStore()
  const isPriceList: boolean = cart === ECarts.ct_price_list_create

  /*
   * The price list is organised in PROJECTS, and the project management is its entry point: with no
   * project open the page shows the projects instead of the list. A plain call of the page starts
   * there: nothing is open until openFromUrl finds a project in the address.
   *
   * Which project is open stands in the url (`tab`), so F5 and a bookmark come back to it and the
   * browser's back button reaches the management — `useProjectOpen` holds that end of it.
   *
   * The "Cart / Management" tab pair is gone; the way back is the "To project management" button in
   * the header, which closes the open project.
   *
   * ⚑ Only the price list works this way. Every other cart is untouched.
   */
  const currentProject: Ref<IOpenProject | undefined> = ref(undefined)
  /*
   * ⚑ Read from the URL, not from `currentProject` — the address is what says which project is open,
   * and back/forward change it without this component being mounted again. A computed follows that by
   * itself; no watcher has to notice it.
   *
   * Asking `currentProject` as well would look safer and costs two requests: while a project called up
   * directly is still loading, it is empty, the management would mount for that moment and fire
   * CART_LIST and EXP_PRICELIST for nothing.
   */
  const showProjects: ComputedRef<boolean> = computed(() => isPriceList && !ut_wilo_projectIdFromUrl())

  // Saves the open project by itself, and creates it on the server the moment it comes into being. Idle
  // for every other cart, because it does nothing without an open project.
  const autoSave = useProjectAutoSave(cart, currentProject)

  // Opening one, and the address that goes with it. Idle for every other cart.
  const projectOpen = useProjectOpen(cart)

  // Shown next to the project name, below the buttons: while saving, and when saving fails. "Saved" is
  // no longer shown (24.09.2026, Christoph) — saving is the normal state and needs no label; a failure
  // still does, or nobody would notice that the work is not on the server.
  // Fixed English on purpose, no text keys (25.09.2026, Christoph) — txt_pricelist-saving / -save-error were dropped.
  const saveLabel: ComputedRef<string> = computed(() => {
    switch (autoSave.saveState.value) {
      case 'saving': return 'Saving…'
      case 'error': return 'Not saved — retrying'
      default: return ''
    }
  })

  // Renaming the open project — the name line (CartProjectName) asks, auto-save writes it.
  async function rename(name: string): Promise<void> {
    await autoSave.rename(name)
  }

  /*
   * "Enable edit" on a template project. Unlocking it is the moment it comes into being — auto-save
   * writes it out right afterwards — so this is where the name is asked (31.08.2026, customer
   * request); the empty project is asked in `CartManage`, at its own such moment.
   *
   * ⚑ Only on the way IN, and only while the project has no id. Locking it again, and unlocking one
   * that is already on the server, change nothing that needs a name — asking there would put a modal
   * in front of a button people use to protect the list from their own clicks.
   */
  const nameAsk: Ref<boolean> = ref(false)

  function setEditable(editable: boolean): void {
    if (!currentProject.value) { return }
    if (editable && !currentProject.value.id) {
      nameAsk.value = true
      return
    }
    currentProject.value.editable = editable
  }

  // Name first, then unlocked: the auto-save watcher fires on `editable`, so the other order would
  // create the project under its generated name and rename it a moment later.
  function confirmName(name: string): void {
    nameAsk.value = false
    if (!currentProject.value) { return }
    currentProject.value.name = name
    currentProject.value.editable = true
  }

  /*
   * True while an opening is under way here — handed to the management, which takes no second click
   * meanwhile (see `CartManage#blocked`).
   */
  const opening: Ref<boolean> = ref(false)

  /*
   * Opening a project from the management. The address follows WITH a history entry — that entry is
   * what the browser's back button follows to get back here.
   *
   * A custom price list arrives editable and without an id: confirming its name is the moment it comes
   * into being, so it is created HERE, before it opens, and the address carries its uuid from the
   * start. The placeholder `new` belongs to a locked template only.
   *
   * ⚑ Created first, opened second — on purpose. Opening it without an id and leaving the create to the
   * auto-save watcher raced the address: the navigation to `tab=new` was not through yet, the save gate
   * (`ut_wilo_projectUrlMatches`) saw no match and dropped the save without a word, so the project stood
   * on `tab=new` until its first change, and a language switch or F5 before that threw it away
   * (15.09.2026).
   *
   * If the create fails, nothing opens and the user stays in the management — a project that is not
   * saved cannot be called up again anyway.
   */
  async function openProject(project: IOpenProject): Promise<void> {
    if (project.editable && !project.id) {
      opening.value = true
      try { project.id = await autoSave.create(project) }
      finally { opening.value = false }
      if (!project.id) { return }
    }
    currentProject.value = project
    projectOpen.show(project)
  }

  /*
   * Let go of the project still held in memory, BEFORE the cart is filled with something else. Every
   * way in from the management calls this first: a saved project, a template, a custom price list.
   *
   * ⚑ Measured on 15.09.2026, and the reason this exists: going back leaves the project in memory (so
   * forward shows it again without loading). Opening another one then filled the cart while the old one
   * still counted as open — only the address gate stood between that cart and the old record. The
   * browser's forward button, pressed while the new project was still loading, put the old address back:
   * gate passed, and project A was saved with project B's rows. Without an open project auto-save writes
   * nothing at all, whatever the address says; the gate stays as the second line.
   */
  function releaseProject(): void {
    currentProject.value = undefined
  }

  /*
   * A saved project asked for in the management. Loaded HERE, not there: the management can be unmounted
   * before the load answers (forward to a project address hides it), and its `open` would reach nobody —
   * the cart would hold the new project with nothing open. This component stays, so the opening always
   * ends in the project that was clicked.
   *
   * Nothing came back — deleted meanwhile, or another account's: the management stays as it is, and its
   * list shows what the server answers, which says it better than a message would.
   */
  async function openSaved(asked: { id: string, name: string }): Promise<void> {
    if (opening.value) { return }
    opening.value = true
    releaseProject()
    try {
      const project: IOpenProject | undefined = await projectOpen.loadById(asked.id)
      if (project) { await openProject({ ...project, name: project.name || asked.name }) }
    }
    finally { opening.value = false }
  }

  // Back to the projects. Write out what is still pending first: the debounce may not have fired yet,
  // and closing must not be the one action that loses the last change.
  //
  // The address is cleared by REPLACE, not push: leaving is the deliberate undoing of the opening, and
  // a step of its own would mean the back button walks straight back into the project the user just
  // left. The entry pushed when opening is overwritten by it, so back leads where it led before.
  async function closeProject(): Promise<void> {
    await autoSave.flush()
    currentProject.value = undefined
    ut_wilo_setProjectUrl('', false)
  }

  /*
   * The project to open from the url, on the page's first mount — called by Cart.vue in its own
   * onBeforeMount, at the place the order there asks for. Safe at that moment: the cart is emptied and
   * refreshed by appIndex.ts BEFORE the cart page is mounted (it is lazy), so nothing that arrives
   * afterwards overwrites what is loaded — the trap described in the ai_memory entry
   * `reference_cart_hydration_transient`.
   *
   * The cart is not emptied here the way LCC's is, and needs not be (31.08.2026): it is listed in
   * `tabLocalCarts`, so it is never written to local storage nor restored from it. Emptying here would
   * not even hold — it IS a cart page, and `updateCart` assigns the server's answer back afterwards
   * (measured 24.08.2026).
   */
  async function openFromUrl(): Promise<void> {
    if (!isPriceList) { return }
    // The ordinary loading overlay of the app covers the moment between first render and answer —
    // all of it now: before the counted loading state (14.10.2026) the CART_READ inside switched it
    // off on its own return, before the cart was refreshed.
    currentProject.value = await indexStore.withLoading(() => projectOpen.openFromUrl())
  }

  /*
   * Leaving a project through the browser's back button. The route changes without this component
   * being mounted again, so nothing else would write out what the debounce is still holding — the
   * "To project management" button flushes by itself, the back button has nobody to do it.
   *
   * ⚑ The ROUTER hook, not a watcher on the store: the address decides what is open, so what hangs on
   * a change of address asks the router (see the ai_memory entry `projekt_client_architektur`,
   * "GRUNDKONZEPT"). It runs while the old url is still in place, which is what auto-save needs — its
   * own gate compares the open project against exactly that.
   */
  onBeforeRouteUpdate(async (to) => {
    if (!isPriceList || !currentProject.value) { return }
    const stillMine: boolean = (to.query.tab || '') === (currentProject.value.id || def_newProjectUrlId)
    if (!stillMine) { await autoSave.flush() }
  })

  return {
    currentProject,
    showProjects,
    saveLabel,
    opening,
    nameAsk,
    rename,
    setEditable,
    confirmName,
    openProject,
    releaseProject,
    openSaved,
    closeProject,
    openFromUrl,
  }
}
